import { FC, useContext, useState } from 'react';
import { PageState } from '@/model/common/PageState';

import './documents.scss';
import DocumentService from '@/api/documentService/DocumentService';
import { AppContext } from '@/context/AppContext';
import { b64toBlob } from '@/_helper/formatBlob';
import FileSaver from 'file-saver';
import { Icon } from '@/comp/icon/icon';

type DocumentTileProps = {
    label: string;
    label_loading?: string;
    service: string;
    name: string;
    link?: string;
    documentType?: string;
    errorHandling?: (error: boolean) => void;
};

type DocumentType = {
    documentType: string;
    name: string;
    type: 'application/pdf';
    content: string;
};

export const DocumentTile: FC<DocumentTileProps> = ({
    label,
    label_loading,
    name,
    service,
    documentType,
    link = false,
    errorHandling,
}) => {
    const [pageState, setPageState] = useState<PageState>(PageState.Idle);
    const [file, setFile] = useState<DocumentType[]>([]);
    const { bookingData } = useContext(AppContext);

    const loadFile = () => {
        if (file.length === 0) {
            setPageState(PageState.Loading);
            DocumentService.getDocument(String(bookingData?.bookingId), service, documentType ?? '')
                .then((data) => {
                    setPageState(PageState.Idle);
                    let files: DocumentType[] = [];
                    if (data?.data?.documents.length) {
                        files = data.data.documents.filter(
                            (item: { documentType: string }) => item.documentType === name
                        );
                    }

                    if (!files.length) {
                        handleError();
                        return;
                    }
                    setFile(files);

                    if (window.$gtmTracking) {
                        window.$gtmTracking.gtag('event', 'download_document', {
                            source: link ? 'home' : 'documents',
                            name: name,
                        });
                    }
                    files.forEach((file) => {
                        const blob = b64toBlob(file.content, 'application/octet-stream', 1024);
                        FileSaver.saveAs(blob, file.name);
                    });
                })
                .catch(() => {
                    handleError();
                });
        } else {
            file.forEach((file) => {
                const blob = b64toBlob(file.content, 'application/octet-stream', 1024);
                FileSaver.saveAs(blob, file.name);
            });
        }
    };

    const handleError = () => {
        setPageState(PageState.Error);
        if (typeof errorHandling === 'function') {
            errorHandling(true);
        }
    };

    if (link) {
        return (
            <>
                <div className={`button-element button-ghost document-container-link`} onClick={loadFile}>
                    <span className="document-title">
                        {pageState === PageState.Loading && label_loading ? label_loading : label}
                    </span>
                    {pageState === PageState.Loading && <span className="loader petrol"></span>}
                </div>
                {!errorHandling && pageState === PageState.Error && <ErrorText />}
            </>
        );
    }

    return (
        <div className="document-container">
            <div className={`document ${pageState}`} onClick={loadFile}>
                <Icon name="pictureAsPdf" size={30} className="icon-pdf" ariaHidden />
                <span className="document-title">{label}</span>
                {pageState === PageState.Loading ? (
                    <span className="loader petrol"></span>
                ) : pageState === PageState.Error ? (
                    <Icon name="report" size={30} ariaHidden />
                ) : (
                    <Icon name="arrowForwardIos" size={22} ariaHidden />
                )}
            </div>
            {pageState === PageState.Error && <ErrorText />}
        </div>
    );
};

const ErrorText = () => (
    <p className="error-text">
        Hier ist wohl etwas schiefgelaufen! Bitte versuchen Sie es erneut oder wenden Sie sich an unser Serviceteam.
    </p>
);
export default DocumentTile;
